import React, { useState, useEffect } from 'react';
import Card from '../common/Card';
import '../../styles/shopify-profit.css';
import TableMobile, { TableMobileColumn, StatusTone } from '../responsive-fixes/TableMobile';
import { useResponsiveLayout } from '../responsive-fixes/useResponsiveLayout';

const truncate = (text: string, max: number = 55): string => {
  if (!text) return '';
  return text.length > max ? text.slice(0, max) + '...' : text;
};

interface OrderRowProps {
  row: any;
  formatCurrency: (value: number) => string;
}

const OrderRow: React.FC<OrderRowProps> = ({ row, formatCurrency }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <tr onClick={() => setOpen(!open)} style={{ cursor: 'pointer' }}>
        <td>{row.order_number}</td>
        <td>{row.order_date}</td>
        <td title={row.product_name}>{truncate(row.product_name, 55)}</td>
        <td>{row.channel_source}</td>
        <td>{row.qty}</td>
        <td>{formatCurrency(row.sold)}</td>
        <td className={row.shipping_upsell > 0 ? 'positive' : ''}>
          {formatCurrency(row.shipping_upsell)}
        </td>
        <td className={row.product_upsell > 0 ? 'positive' : ''}>
          {formatCurrency(row.product_upsell)}
        </td>
        <td>{formatCurrency(row.cost_price)}</td>
        <td className={row.profit > 0 ? 'profit' : ''}>
          {formatCurrency(row.profit)}
        </td>
      </tr>

      {open && (
        <tr className="expanded-row">
          <td colSpan={10} className="expanded-content">
            <div className="detail-grid">
              <div><strong>Full Product Name:</strong> {row.product_name}</div>
              <div><strong>SKU:</strong> {row.sku || '-'}</div>
              <div><strong>Channel Group:</strong> {row.channel_group || '-'}</div>
              <div><strong>Shipping Type:</strong> {row.shipping_type || 'Not Applicable'}</div>
              <div><strong>Shipping Charged:</strong> {formatCurrency(row.shipping_charged)}</div>
              <div><strong>Shipping Cost:</strong> {formatCurrency(row.shipping_cost)}</div>
              <div><strong>Discount:</strong> {formatCurrency(row.discount_amount)}</div>
              <div><strong>Fulfillment Date:</strong> {row.fulfillment_date || '-'}</div>
              <div><strong>Customer:</strong> {row.customer_name || '-'}</div>
              <div><strong>City:</strong> {row.shipping_city || '-'}</div>
              <div><strong>Is Upsell Item:</strong> {row.is_upsell_item ? 'Yes' : 'No'}</div>
              <div className="formula-trace"><strong>Formula:</strong> {row.formula_trace || '-'}</div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

interface ProfitSummary {
  total_orders: number;
  total_line_items: number;
  gross_sales: number;
  discounts: number;
  net_sales: number;
  shipping_collected: number;
  shipping_cost: number;
  shipping_upsell: number;
  product_upsell: number;
  returns: number;
  total_cost: number;
  total_expenses: number;
  total_profit: number;
  cost_available: boolean;
  profit_available: boolean;
}

interface ChannelData {
  orders: number;
  line_items: number;
  sold: number;
  shipping_upsell: number;
  product_upsell: number;
  returns: number;
  cost: number;
  profit: number;
}

interface ProfitReport {
  status: string;
  period: { from: string; to: string };
  filter: string;
  summary: ProfitSummary;
  online: ChannelData;
  pos: ChannelData;
  rows: any[];
  raw_order_count: number;
}

const ShopifyProfitReport: React.FC = () => {
  const responsive = useResponsiveLayout();
  const useMobileTable = responsive.isPhone || responsive.isPhablet;
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [channel, setChannel] = useState<'all' | 'online' | 'pos'>('all');
  const [report, setReport] = useState<ProfitReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `/api/shopify/profit?from=${fromDate}&to=${toDate}&channel=${channel}`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch profit report');
      }
      const data = await response.json();
      setReport(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return 'N/A';
    return `AED ${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatNumber = (value: number): string => {
    return value.toLocaleString('en-US');
  };

  const downloadCSV = () => {
    if (!report || !report.rows) return;
    
    const csvContent = convertToCSV(report.rows);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `profit_report_${fromDate}_to_${toDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const convertToCSV = (rows: any[]): string => {
    if (!rows.length) return '';
    
    const headers = Object.keys(rows[0]);
    const csvRows = [
      headers.join(','),
      ...rows.map(row => 
        headers.map(header => {
          const value = row[header];
          return typeof value === 'string' && value.includes(',') 
            ? `"${value}"` 
            : value;
        }).join(',')
      )
    ];
    return csvRows.join('\n');
  };

  return (
    <div className="profit-report-container">
      <div className="profit-header">
        <div className="profit-header-content">
          <img 
            src="https://cdn.shopify.com/s/files/1/0911/0215/0954/files/Afaq_official_logo.png?v=1770887488" 
            alt="Afaq Alnaseem Trading LLC Logo" 
            className="profit-logo"
          />
          <h1 className="profit-title">Shopify Profit Report</h1>
        </div>
      </div>

      <Card title="Date Range & Filters">
        <div className="profit-filters">
          <div className="filter-group">
            <label htmlFor="fromDate" className="filter-label">From Date</label>
            <input
              type="date"
              id="fromDate"
              name="fromDate"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="filter-input"
            />
          </div>
          <div className="filter-group">
            <label htmlFor="toDate" className="filter-label">To Date</label>
            <input
              type="date"
              id="toDate"
              name="toDate"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="filter-input"
            />
          </div>
          <div className="filter-group">
            <label htmlFor="channel" className="filter-label">Channel</label>
            <select
              id="channel"
              name="channel"
              value={channel}
              onChange={(e) => setChannel(e.target.value as 'all' | 'online' | 'pos')}
              className="filter-select"
            >
              <option value="all">All Channels</option>
              <option value="online">Online Only</option>
              <option value="pos">POS Only</option>
            </select>
          </div>
          <button onClick={fetchReport} className="btn-primary filter-btn" disabled={loading}>
            {loading ? 'Loading...' : 'Generate Report'}
          </button>
          {report && (
            <button onClick={downloadCSV} className="btn-secondary filter-btn">
              Download CSV
            </button>
          )}
        </div>
      </Card>

      {error && (
        <div className="profit-error">
          <p>⚠️ {error}</p>
        </div>
      )}

      {loading && (
        <div className="profit-loading">
          <div className="loading-spinner"></div>
          <p>Fetching profit data from Shopify...</p>
        </div>
      )}

      {report && report.status === 'success' && (
        <>
          <div className="profit-summary-grid">
            <Card title="📊 Overall Summary">
              <div className="summary-stats">
                <div className="stat-row">
                  <span className="stat-label">Total Orders:</span>
                  <span className="stat-value">{formatNumber(report.summary.total_orders)}</span>
                </div>
                <div className="stat-row">
                  <span className="stat-label">Line Items:</span>
                  <span className="stat-value">{formatNumber(report.summary.total_line_items)}</span>
                </div>
                <div className="stat-row highlight">
                  <span className="stat-label">Gross Sales:</span>
                  <span className="stat-value">{formatCurrency(report.summary.gross_sales)}</span>
                </div>
                <div className="stat-row">
                  <span className="stat-label">Discounts:</span>
                  <span className="stat-value negative">-{formatCurrency(report.summary.discounts)}</span>
                </div>
                <div className="stat-row highlight">
                  <span className="stat-label">Net Sales:</span>
                  <span className="stat-value">{formatCurrency(report.summary.net_sales)}</span>
                </div>
              </div>
            </Card>

            <Card title="🚚 Shipping & Upsells">
              <div className="summary-stats">
                <div className="stat-row">
                  <span className="stat-label">Shipping Collected:</span>
                  <span className="stat-value">{formatCurrency(report.summary.shipping_collected)}</span>
                </div>
                <div className="stat-row">
                  <span className="stat-label">Shipping Cost:</span>
                  <span className="stat-value negative">-{formatCurrency(report.summary.shipping_cost)}</span>
                </div>
                <div className="stat-row positive">
                  <span className="stat-label">Shipping Upsell:</span>
                  <span className="stat-value">{formatCurrency(report.summary.shipping_upsell)}</span>
                </div>
                <div className="stat-row positive">
                  <span className="stat-label">Product Upsell:</span>
                  <span className="stat-value">{formatCurrency(report.summary.product_upsell)}</span>
                </div>
                <div className="stat-row">
                  <span className="stat-label">Returns:</span>
                  <span className="stat-value negative">-{formatCurrency(report.summary.returns)}</span>
                </div>
              </div>
            </Card>

            <Card title="💰 Cost & Profit">
              <div className="summary-stats">
                <div className="stat-row">
                  <span className="stat-label">Total Cost:</span>
                  <span className="stat-value negative">
                    {report.summary.cost_available 
                      ? `-${formatCurrency(report.summary.total_cost)}`
                      : 'N/A'}
                  </span>
                </div>
                <div className="stat-row">
                  <span className="stat-label">Total Expenses:</span>
                  <span className="stat-value negative">-{formatCurrency(report.summary.total_expenses)}</span>
                </div>
                <div className="stat-row profit-highlight">
                  <span className="stat-label">Total Profit:</span>
                  <span className="stat-value profit">
                    {report.summary.profit_available 
                      ? formatCurrency(report.summary.total_profit)
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </Card>
          </div>

          <div className="channel-breakdown">
            <Card title="🌐 Online Channel">
              <div className="channel-stats">
                <div className="stat-item">
                  <div className="stat-label">Orders</div>
                  <div className="stat-value">{formatNumber(report.online.orders)}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">Sold</div>
                  <div className="stat-value">{formatCurrency(report.online.sold)}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">Upsells</div>
                  <div className="stat-value positive">
                    {formatCurrency(report.online.shipping_upsell + report.online.product_upsell)}
                  </div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">Profit</div>
                  <div className="stat-value profit">{formatCurrency(report.online.profit)}</div>
                </div>
              </div>
            </Card>

            <Card title="🏪 POS Channel">
              <div className="channel-stats">
                <div className="stat-item">
                  <div className="stat-label">Orders</div>
                  <div className="stat-value">{formatNumber(report.pos.orders)}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">Sold</div>
                  <div className="stat-value">{formatCurrency(report.pos.sold)}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">Upsells</div>
                  <div className="stat-value positive">
                    {formatCurrency(report.pos.shipping_upsell + report.pos.product_upsell)}
                  </div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">Profit</div>
                  <div className="stat-value profit">{formatCurrency(report.pos.profit)}</div>
                </div>
              </div>
            </Card>
          </div>

          {report.rows && report.rows.length > 0 && (
            <Card title="📋 Detailed Orders">
              {useMobileTable ? (
                // M3 fix (audit): 10-column profit table is unreadable below md.
                // Card-view via TableMobile; desktop table unchanged.
                <TableMobile<any>
                  columns={[
                    { key: 'product_name', label: 'Product', primary: true,
                      format: (v, row) => `#${row.order_number} · ${truncate((v as string) || '', 40)}`,
                    },
                    { key: 'profit', label: 'Profit', status: true,
                      format: (v) => formatCurrency(v as number),
                    },
                    { key: 'order_date', label: 'Date' },
                    { key: 'channel_source', label: 'Channel' },
                    { key: 'qty', label: 'Qty' },
                    { key: 'sold', label: 'Sold', format: (v) => formatCurrency(v as number) },
                    { key: 'shipping_upsell', label: 'Ship Up', format: (v) => formatCurrency(v as number) },
                    { key: 'product_upsell', label: 'Prod Up', format: (v) => formatCurrency(v as number) },
                    { key: 'cost_price', label: 'Cost', format: (v) => formatCurrency(v as number) },
                  ] as TableMobileColumn<any>[]}
                  rows={report.rows}
                  statusTone={(row): StatusTone =>
                    (row.profit || 0) > 0 ? 'success' : (row.profit || 0) < 0 ? 'danger' : 'muted'
                  }
                />
              ) : (
                <div className="orders-table-container">
                  <table className="orders-table">
                    <thead>
                      <tr>
                        <th>Order #</th>
                        <th>Date</th>
                        <th>Product</th>
                        <th>Channel</th>
                        <th>Qty</th>
                        <th>Sold</th>
                        <th>Ship Upsell</th>
                        <th>Product Upsell</th>
                        <th>Cost</th>
                        <th>Profit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.rows.map((row, index) => (
                        <OrderRow key={index} row={row} formatCurrency={formatCurrency} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}
        </>
      )}

      {report && report.status === 'not_configured' && (
        <Card title="⚠️ Configuration Required">
          <p className="config-warning">
            Shopify credentials not configured. Please set SHOPIFY_STORE_DOMAIN and SHOPIFY_ACCESS_TOKEN environment variables.
          </p>
        </Card>
      )}
    </div>
  );
};

export default ShopifyProfitReport;
